import React from "react";

import AutocompleteSelectInput from "veneer/AutocompleteSelectInput";

import { StringOption } from "cortex/ui/src/ui/table/types";
import { arrayToSelectOptions } from "cortex/ui/src/util/select";

interface FormField {
  displayName?: string;
  name: string;
  value?: string | string[] | null;
}

interface Schema {
  is_creatable?: boolean;
  options?: string[] | null;
}

interface SelectFieldProps {
  error?: boolean;
  field: FormField;
  handleChange: (value: string | string[] | null) => void;
  helperText?: string | null;
  isMulti: boolean;
  isRequired: boolean;
  readOnly: boolean;
  schema: Schema;
}

function getOptionValue(option: StringOption | string): string {
  return typeof option === "string" ? option : option.value;
}

function getSelectValue(
  rawValue: FormField["value"],
  isMulti: boolean,
): StringOption | StringOption[] | null {
  if (rawValue === null || rawValue === undefined) {
    return isMulti ? [] : null;
  }

  const value = Array.isArray(rawValue) ? rawValue : [rawValue];

  if (isMulti) {
    return arrayToSelectOptions(value) as StringOption[];
  }

  return (arrayToSelectOptions(value)[0] as StringOption) || null;
}

const SelectField = ({
  error = false,
  field,
  handleChange,
  helperText,
  isMulti,
  isRequired,
  readOnly,
  schema,
}: SelectFieldProps) => {
  const { is_creatable: isCreatable = false, options: schemaOptions } = schema;
  const options = arrayToSelectOptions(schemaOptions || []) as StringOption[];
  const selectValue = getSelectValue(field.value, isMulti);

  const onChange = (
    _event: unknown,
    value: string | StringOption | Array<string | StringOption> | null,
  ): void => {
    if (Array.isArray(value)) {
      handleChange(value.map(getOptionValue));
      return;
    }

    handleChange(value ? getOptionValue(value) : null);
  };

  return (
    <AutocompleteSelectInput<StringOption, boolean, undefined, boolean>
      dataTestId={`${field.name}.SelectInput`}
      disabled={readOnly}
      freeSolo={isCreatable}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.label
      }
      inputProps={{
        error,
        helperText: helperText ?? undefined,
        label: field.displayName,
        required: isRequired,
        size: "small",
      }}
      isOptionEqualToValue={(option, selectedValue) =>
        getOptionValue(option) === getOptionValue(selectedValue)
      }
      multiple={isMulti}
      onChange={onChange as any}
      options={options}
      value={selectValue}
    />
  );
};

export { SelectField };
